/**
 * Copyright © 2025 Delicious AI, LLC
 */

export const PLANOGRAM_SHELF_CLIPBOARD_KEY = 'ingen:planogram-shelf-clipboard'

export interface PlanogramShelfClipboardShelf {
  shelfIndex: number
  upcs: string[]
}

export interface PlanogramShelfClipboard {
  version: 1
  copiedAt: string
  sourceDisplayId: string
  sourceDisplayName: string
  sourceStoreName: string
  shelves: PlanogramShelfClipboardShelf[]
}

export interface WriteShelfClipboardArgs {
  sourceDisplayId: string
  sourceDisplayName: string
  sourceStoreName: string
  shelves: PlanogramShelfClipboardShelf[]
}

const isPlanogramShelfClipboard = (
  value: unknown,
): value is PlanogramShelfClipboard => {
  if (!value || typeof value !== 'object') {
    return false
  }

  const clipboard = value as PlanogramShelfClipboard

  return (
    clipboard.version === 1 &&
    typeof clipboard.copiedAt === 'string' &&
    typeof clipboard.sourceDisplayId === 'string' &&
    typeof clipboard.sourceDisplayName === 'string' &&
    typeof clipboard.sourceStoreName === 'string' &&
    Array.isArray(clipboard.shelves) &&
    clipboard.shelves.every(
      shelf =>
        typeof shelf.shelfIndex === 'number' &&
        Array.isArray(shelf.upcs) &&
        shelf.upcs.every(upc => typeof upc === 'string'),
    )
  )
}

export const writeShelfClipboard = ({
  sourceDisplayId,
  sourceDisplayName,
  sourceStoreName,
  shelves,
}: WriteShelfClipboardArgs): void => {
  const clipboard: PlanogramShelfClipboard = {
    version: 1,
    copiedAt: new Date().toISOString(),
    sourceDisplayId,
    sourceDisplayName,
    sourceStoreName,
    shelves,
  }

  let serialized: string

  try {
    serialized = JSON.stringify(clipboard)
  } catch (error) {
    console.error('Failed to serialize planogram shelf clipboard', {
      key: PLANOGRAM_SHELF_CLIPBOARD_KEY,
      clipboard,
      error,
    })
    return
  }

  try {
    localStorage.setItem(PLANOGRAM_SHELF_CLIPBOARD_KEY, serialized)
    sessionStorage.removeItem(PLANOGRAM_SHELF_CLIPBOARD_KEY)
    return
  } catch (error) {
    console.error('Failed to write planogram shelf clipboard to localStorage', {
      key: PLANOGRAM_SHELF_CLIPBOARD_KEY,
      clipboard,
      error,
    })
  }

  try {
    sessionStorage.setItem(PLANOGRAM_SHELF_CLIPBOARD_KEY, serialized)
  } catch (error) {
    console.error(
      'Failed to write planogram shelf clipboard to sessionStorage',
      {
        key: PLANOGRAM_SHELF_CLIPBOARD_KEY,
        clipboard,
        error,
      },
    )
  }
}

export const readShelfClipboard = (): PlanogramShelfClipboard | null => {
  const raw =
    localStorage.getItem(PLANOGRAM_SHELF_CLIPBOARD_KEY) ??
    sessionStorage.getItem(PLANOGRAM_SHELF_CLIPBOARD_KEY)

  if (!raw) {
    return null
  }

  try {
    const parsed: unknown = JSON.parse(raw)

    if (!isPlanogramShelfClipboard(parsed)) {
      return null
    }

    return parsed
  } catch {
    return null
  }
}

export const hasShelfClipboard = (): boolean => {
  const clipboard = readShelfClipboard()

  return (
    clipboard !== null && clipboard.shelves.some(shelf => shelf.upcs.length > 0)
  )
}

export const clearShelfClipboard = (): void => {
  localStorage.removeItem(PLANOGRAM_SHELF_CLIPBOARD_KEY)
  sessionStorage.removeItem(PLANOGRAM_SHELF_CLIPBOARD_KEY)
}

export const getClipboardUpcs = (
  clipboard: PlanogramShelfClipboard,
): string[] => {
  return clipboard.shelves.flatMap(shelf => shelf.upcs)
}
